import {addAndRunAnalysisSteps} from 'analysis-tool/AnalysisActions';
import {
  SINGLE_DATASET_INPUT_STEP_DATA_NAME,
  SINGLE_DATASET_OUTPUT_STEP_DATA_NAME,
} from 'analysis-tool/AnalysisStepDataNames';
import {AnalysisGraphQL, GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import {
  AnalysisMainViewItem,
  AnalysisMainViewItemType,
  DataReference,
} from 'analysis-tool/ui/main-view/MainViewTypes';
import getConfig from 'getConfig';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {VISUALIZATION_TYPE_TO_READABLE} from 'insights/analytics-visualizations/AnalyticsVisualizationConstants';
import getVisualizationType from 'insights/analytics-visualizations/data/getVisualizationType';
import {getDefaultVisualizationNameForVisualizationType} from 'insights/analytics-visualizations/getDefaultVisualizationNameForVisualizationType';
import {
  VisualizationType,
  VisualizationConfig,
} from 'insights/universal-blocks/SharedUniversalBlockViewTypes';
import {withLoadingScreen} from 'util/Promises';

import getPersistedVisualizationConfig, {
  getPersistedChartViewConfig,
} from './charting/getPersistedVisualizationConfig';

/** @package */
export async function createViewAndVisualizationSteps(
  analysis: AnalysisGraphQL,
  inputDataFrameId: string,
  inputDataReference: DataReference,
  initialVisualizationConfig: VisualizationConfig
): Promise<Array<GetAnalysisPipelineStep>> {
  return await addAndRunAnalysisSteps(analysis.id, [
    {
      stepType: PipelineStepType.DATASET_VIEW,
      stepVersion: 0,
      stepConfig: {},
      stepInputParents: {
        [SINGLE_DATASET_INPUT_STEP_DATA_NAME]: [inputDataReference],
      },
      stepInput: {inputDataFrameId},
      runStep: true,
    },
    {
      stepType: PipelineStepType.VISUALIZATION,
      stepVersion: 0,
      stepConfig: {
        visualizationConfig: getPersistedVisualizationConfig(initialVisualizationConfig),
        visualizationName: getDefaultVisualizationNameForVisualizationType(
          initialVisualizationConfig.parameters.__typename
        ),
      },
      stepInputParents: {
        [SINGLE_DATASET_INPUT_STEP_DATA_NAME]: [{stepDataName: SINGLE_DATASET_OUTPUT_STEP_DATA_NAME}],
      },
      runStep: true,
    },
  ]);
}

export async function createViewAndOpenVisualizationView(
  analysis: AnalysisGraphQL,
  inputDataFrameId: string,
  inputDataReference: DataReference,
  onOpenStepInitializationView: (newSelectedItem: AnalysisMainViewItem) => void,
  initialVisualizationConfig: VisualizationConfig
): Promise<void> {
  const newSteps = await withLoadingScreen<Array<GetAnalysisPipelineStep>>(
    createViewAndVisualizationSteps(
      analysis,
      inputDataFrameId,
      inputDataReference,
      initialVisualizationConfig
    ),
    'Creating view...'
  );

  onOpenStepInitializationView({
    type: AnalysisMainViewItemType.EDIT_VISUALIZATION,
    stepId: newSteps[newSteps.length - 1].id,
  });
}

/** @package */
export async function createViewAndChartViewSteps(
  analysis: AnalysisGraphQL,
  inputDataFrameId: string,
  inputDataReference: DataReference,
  initialVisualizationConfig: VisualizationConfig
): Promise<Array<GetAnalysisPipelineStep>> {
  return await addAndRunAnalysisSteps(analysis.id, [
    {
      stepType: PipelineStepType.DATASET_VIEW,
      stepVersion: 0,
      stepConfig: {},
      stepInputParents: {
        [SINGLE_DATASET_INPUT_STEP_DATA_NAME]: [inputDataReference],
      },
      stepInput: {inputDataFrameId},
      runStep: true,
    },
    {
      stepType: PipelineStepType.CHART_VIEW,
      stepVersion: 0,
      stepConfig: {
        chartConfig: getPersistedChartViewConfig(initialVisualizationConfig),
        chartName: getDefaultVisualizationNameForVisualizationType(
          initialVisualizationConfig.parameters.__typename
        ),
      },
      stepInputParents: {
        [SINGLE_DATASET_INPUT_STEP_DATA_NAME]: [{stepDataName: SINGLE_DATASET_OUTPUT_STEP_DATA_NAME}],
      },
      runStep: true,
    },
  ]);
}

/** @package */
export async function createViewAndTableViewSteps(
  analysis: AnalysisGraphQL,
  inputDataFrameId: string,
  inputDataReference: DataReference
): Promise<Array<GetAnalysisPipelineStep>> {
  return await addAndRunAnalysisSteps(analysis.id, [
    {
      stepType: PipelineStepType.DATASET_VIEW,
      stepVersion: 0,
      stepConfig: {},
      stepInputParents: {
        [SINGLE_DATASET_INPUT_STEP_DATA_NAME]: [inputDataReference],
      },
      stepInput: {inputDataFrameId},
      runStep: true,
    },
    {
      stepType: PipelineStepType.TABLE_VIEW,
      stepVersion: 0,
      stepConfig: {
        viewName: VISUALIZATION_TYPE_TO_READABLE[VisualizationType.TABLE],
      },
      stepInputParents: {
        [SINGLE_DATASET_INPUT_STEP_DATA_NAME]: [{stepDataName: SINGLE_DATASET_OUTPUT_STEP_DATA_NAME}],
      },
      runStep: true,
    },
  ]);
}

export async function createViewAndOpenChartOrTableView(
  analysis: AnalysisGraphQL,
  inputDataFrameId: string,
  inputDataReference: DataReference,
  onOpenStepInitializationView: (newSelectedItem: AnalysisMainViewItem) => void,
  initialVisualizationConfig: VisualizationConfig
): Promise<void> {
  const newSteps = await withLoadingScreen<Array<GetAnalysisPipelineStep>>(
    getVisualizationType(initialVisualizationConfig) !== VisualizationType.TABLE
      ? createViewAndChartViewSteps(
          analysis,
          inputDataFrameId,
          inputDataReference,
          initialVisualizationConfig
        )
      : createViewAndTableViewSteps(analysis, inputDataFrameId, inputDataReference),
    'Creating view...'
  );

  onOpenStepInitializationView({
    type: AnalysisMainViewItemType.EDIT_CHART,
    stepId: newSteps[newSteps.length - 1].id,
  });
}

export default async function createViewAndOpenVisualizationViewOrChartView(
  analysis: AnalysisGraphQL,
  inputDataFrameId: string,
  inputDataReference: DataReference,
  onOpenStepInitializationView: (newSelectedItem: AnalysisMainViewItem) => void,
  initialVisualizationConfig: VisualizationConfig
): Promise<void> {
  if (getConfig('ENABLE_ANALYSIS_TOOL_CHARTS_V2')) {
    await createViewAndOpenChartOrTableView(
      analysis,
      inputDataFrameId,
      inputDataReference,
      onOpenStepInitializationView,
      initialVisualizationConfig
    );
    return;
  }

  await createViewAndOpenVisualizationView(
    analysis,
    inputDataFrameId,
    inputDataReference,
    onOpenStepInitializationView,
    initialVisualizationConfig
  );
}
